import { getAllTargetTypes, getTargetTypes, getTitleForType } from "csi/canvas/storeFactory";
import Key from "csi/core/Key";
import SpecBuilder from "csi/core/SpecBuilder";
import locale from "csi/locale";
import csi from "csi/main";
import CustomViewAttributesStore, { CustomViewAttributesLevelParams, CustomViewAttributesOptions } from "csi/middleware/customView/attributes/CustomViewAttributesStore";
import localeProvider from "csi/page/localeProvider";
import reflection from "csi/reflection";
import { getClassEmptyPropertyView } from "csi/service/viewService";
import types from "csi/types";
import { C8NodeType } from "csi/typings";
import { isNullOrUndefined } from "csi/utility/coreUtility";
import xml from "csi/xml";
import { ComponentItem } from "designSystem/types/component-item-types";
import { ICustomViewAttribute } from "src/c8-platform/type/customView";

const NAME = "name";

const _restrictedFormats = {};
_restrictedFormats[types.FORMAT_BARCODE] = 1;

function getSelection(selectedType: string): string[] {
	const keys: Key[] = Key.getKeysForType(selectedType, true);
	const nodeNameKey = keys.filter((key) => key.id === xml.ATTR_NAME);
	return (nodeNameKey.length > 0) ? [nodeNameKey[0].typedString] : [keys[0].typedString];
}

/**
 * Restrict lists and rich text attributes from cards to keep from growing too big and/or performing poorly
 */
function omitAttributeCallback(key: Key): boolean {
	const { index, isDefaultImage, valueFormat, valueType } = key;

	if (isDefaultImage) {
		return false;
	}

	if (_restrictedFormats[valueFormat]) {
		return true;
	}

	if (valueType == xml.ATTR_URL) {
		return true;
	}

	if (xml.attributeTypeIsContainer(valueType) && isNullOrUndefined(index)) {
		return true;
	}

	return false;
}

class AttributesPlaceholderStore {
	attributesStore: CustomViewAttributesStore;
	readonly canvasType: C8NodeType;
	readonly groupObject: object;
	readonly previousAttributeKeys: ICustomViewAttribute[];
	initialSelections: ICustomViewAttribute[];
	types: ComponentItem[];
	selectedType: string;

	/**
	 * create list of types
	 */ 
	initTypes(): void {
		// For type field dynamically get types based on the canvas template type
		const canvasType = xml.enumGetText(this.canvasType);
		let types = [];
		let type: string;
		const contextName = locale.resource("CanvasAttributeContext");
		let sourceType: string;

		if (canvasType) {
			var rc = reflection.getClass(canvasType);
			type = rc.isMaster() ? rc.getRevisionClass().getType() : canvasType;
			sourceType = type;

			// Get all the target types for which paths are registered for revision type,
			// unless not master, then get target types registered for canvas type.
			types = getTargetTypes(type);

			// Add context (auto-fill).
			types.unshift({ id: type, name: contextName });
		} else {
			// No canvas type so get all unique target types, sorted.
			types = getAllTargetTypes();
		}
		if (types.length) {
			types = types.map((type) => {
				// Return context object or object for non-context type.
				const title = getTitleForType(sourceType, type);
				var retVal = (type.name === contextName ? type : { id: type, name: title });
				return retVal;
			});
			types = csi.sort(types, NAME);

			this.types = types;
			this.selectedType = types[0].id;
		}
	};

	/**
	 * create attribute store for selected type
	 */
	createAttributeStore() : void {
		const { previousAttributeKeys, types } = this;
		const domainType = previousAttributeKeys && new Key({ dpkey: previousAttributeKeys[0] as string })?.domainType;

		const levels: CustomViewAttributesLevelParams[] = types.map((type) => {
			const emptyPropView = getClassEmptyPropertyView(reflection.getClass(type.id));
			const {
				m_headings,
				pathHintsContext,
				prefPaths,
			} = emptyPropView;
			return {
				name: type.name,
				selected: domainType === type.id,
				specBuilder: new SpecBuilder(
					type.id,
					null,
					pathHintsContext,
					null,
					prefPaths,
					m_headings,
				),
			};
		});

		// determine options for store
		const attributesOptions: CustomViewAttributesOptions = {
			noGroupSupport: true,
			noMatrixSupport: true,
			noVisibilitySupport: true,
			omitAttributeCallback,
			omitReferencedImages: true,
			onChangeLevel: this.resetSelection.bind(this),
			useNamePropInPlaceOfNodeNameAttr: true,
		};

		this.initialSelections = previousAttributeKeys?.length ? previousAttributeKeys : getSelection(types[0]["id"]);

		// create store
		const attributesStore = this.attributesStore = new CustomViewAttributesStore(localeProvider, attributesOptions);

		// add levels
		levels.forEach(attributesStore.addLevel);

		// add selections
		attributesStore.addSelections(this.initialSelections);
	}

	/**
	 * on change selected type wipe previous attributes selection and select default for new type
	 */
	resetSelection(index: number): void {
		const { attributesStore, types } = this;
		this.selectedType = types[index].id;

		attributesStore.selections.clear();

		const selection = (index === 0 ? this.initialSelections : getSelection(this.selectedType));
		attributesStore.addSelections(selection);

		attributesStore.reevaluateSelections();
	}

	constructor(
		canvasType: string,
		groupObject: object,
		previousAttributeKeys: string[],
	) {
		this.canvasType = canvasType;
		this.groupObject = groupObject;
		this.previousAttributeKeys = previousAttributeKeys;
		this.initTypes();
		this.createAttributeStore();
	}
}

export default AttributesPlaceholderStore;